import { BellRinging01, ChevronRight, XClose } from '@untitledui/icons';
import { useCallback } from 'react';
import { toast } from 'sonner';

import { useCurrentUserEntity } from '@/hooks/useCurrentUserEntity';
import { usePermissions } from '@/hooks/usePermissions';
import { Button } from '@/lib/base/buttons/button';
import { Tooltip } from '@/lib/base/tooltip/tooltip';
import { useNavigateToEntity } from '@/lib/crm/hooks/useNavigateToEntity';
import { logger } from '@/lib/logger';
import {
  resolveReminderSubject,
  resolveReminderTitle,
} from '@/pages/calendar/utils/reminderUtils';
import type { CrmObject } from '@/types/crm';

import { useDismissReminder } from '../hooks/useDismissReminder';

/** Help text for the dashboard Dismiss control on a reminder.
 *
 *  Deliberately promises NO way to undo, because there isn't one: undismiss is
 *  implemented for meetings only (`lib/crm/record/hooks/useUndismissMeeting.ts`),
 *  and there is no reminder equivalent — the endpoint accepts a reminder, but no
 *  UI calls it. Do not add "you can show it again" here until that affordance
 *  exists, or this copy becomes a promise the product does not keep. */
const DISMISS_REMINDER_DESCRIPTION =
  "We'll hide this reminder from your dashboard. Only you see this change.";

interface ReminderRowProps {
  reminder: CrmObject;
  associatedObjects: Record<string, CrmObject>;
}

export function ReminderRow({ reminder, associatedObjects }: ReminderRowProps) {
  const navigateToEntity = useNavigateToEntity();
  const { orgId: orgIdStr } = useCurrentUserEntity();
  const orgId = parseInt(orgIdStr ?? '0', 10);
  const { hasPermission } = usePermissions();
  const { dismissReminder, isDismissing } = useDismissReminder(orgId);

  const { subject, navigationEntityId, navigationEntityTypeName } =
    resolveReminderSubject(reminder, associatedObjects);

  const title = resolveReminderTitle(reminder, subject);

  const handleClick = useCallback(() => {
    if (navigationEntityId) {
      if (!navigationEntityTypeName) {
        // The subject association exists but the subject entity itself isn't
        // in associatedObjects, so we don't know its entity type. navigateToEntity
        // handles undefined safely, but this silent path is otherwise
        // undiagnosable in production — see resolveReminderSubject.
        logger.warn('reminder navigation: subject entity type unknown', {
          reminder_id: reminder.entity_id,
          navigation_entity_id: navigationEntityId,
        });
      }
      navigateToEntity(navigationEntityId, navigationEntityTypeName);
    } else {
      navigateToEntity(reminder.entity_id, 'ahoy_reminder');
    }
  }, [
    navigateToEntity,
    navigationEntityId,
    navigationEntityTypeName,
    reminder.entity_id,
  ]);

  const handleDismiss = useCallback(async () => {
    try {
      await dismissReminder(reminder);
    } catch (error) {
      logger.error(
        'Failed to dismiss reminder',
        error instanceof Error ? error : undefined,
        {
          reminder_id: reminder.entity_id,
          org_id: orgId,
        }
      );
      toast.error("We couldn't dismiss this reminder. Try again.");
    }
  }, [dismissReminder, reminder, orgId]);

  const canDismiss = hasPermission('ahoy_reminder', 'ahoy_property_updated');
  const isDismissingThisReminder = isDismissing(reminder.entity_id);

  return (
    <div className='w-full flex items-center h-14 bg-white dark:bg-neutral-900 border border-[var(--color-border-secondary)] rounded-[4px] hover:bg-neutral-50 dark:hover:bg-neutral-800 transition-colors'>
      <button
        type='button'
        onClick={handleClick}
        className='flex-1 flex items-center gap-3 h-full px-3.5 text-left min-w-0'
      >
        <BellRinging01 className='size-4 text-neutral-400 shrink-0' />
        <span className='flex-1 text-[13px] font-medium text-[var(--color-text-primary)] truncate'>
          {title}
        </span>
        <ChevronRight className='size-4 text-neutral-400 shrink-0' />
      </button>
      {/* Icon-only with a hover tooltip, matching the meeting row: the reminder title
          sits in a `truncate` box, so a labelled button here eats into it. Meaning is
          carried by the tooltip for sighted pointer users and by `aria-label` for
          screen readers and voice control, so nothing depends on hover. */}
      {canDismiss && (
        <Tooltip
          title='Dismiss'
          description={DISMISS_REMINDER_DESCRIPTION}
          placement='top'
        >
          <Button
            color='tertiary'
            size='sm'
            iconLeading={XClose}
            aria-label='Dismiss reminder'
            onClick={handleDismiss}
            isDisabled={isDismissingThisReminder}
            className='mr-2 shrink-0'
          />
        </Tooltip>
      )}
    </div>
  );
}
